import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  EventGuestQuery,
  GuestCheckedInEnvelope,
  GuestCreatedEnvelope,
  GuestDeletedEnvelope,
  GuestEventCanceledEnvelope,
  GuestEventStartsEnvelope,
  GuestOrderCanceledEnvelope,
  GuestUpdatedEnvelope,
  GuestsQueryBuilder,
  QueryEventGuestsOptions,
  QueryEventGuestsResponse,
  queryGuests as universalQueryGuests,
  typedQueryGuests as universalTypedQueryGuests,
} from './events-guests-v1-guest-guests.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function queryGuests(httpClient: HttpClient): QueryGuestsSignature {
  return (options?: QueryEventGuestsOptions) =>
    universalQueryGuests(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryGuestsSignature {
  /**
   * Creates a query to retrieve a list of guests.
   *
   *
   * The `queryGuests()` method builds a query to retrieve a list of guests and returns a [GuestsQueryBuilder](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/ascending) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/find) method.
   *
   * You can refine the query by chaining `GuestsQueryBuilder` methods onto the query. `GuestsQueryBuilder` methods enable you to sort, filter, and control the results that `queryGuests.find()` returns.
   *
   * The query runs with the following `GuestsQueryBuilder` defaults that you can override:
   *
   * - [`skipTo(0)`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/skip-to)
   * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/limit)
   * - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/events/guests/guests-query-builder/descending)
   *
   * The methods that are chained to `queryGuests()` are applied in the order they are called. For example, if you apply `ascending ('_createdDate')` and then `descending ('_updatedDate')`, the results are sorted first by the created date and then, if there are multiple results with the same date, the items are sorted by the updated date.
   *
   * The table below shows which `GuestsQueryBuilder` methods are supported for `queryGuests()`. You can only use one filter method for each property. Only the first filter will work if a property is used in more than one filter.
   */
  (options?: QueryEventGuestsOptions): GuestsQueryBuilder;
}

export function typedQueryGuests(
  httpClient: HttpClient
): TypedQueryGuestsSignature {
  return (query: EventGuestQuery, options?: QueryEventGuestsOptions) =>
    universalTypedQueryGuests(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryGuestsSignature {
  /** @returns A `GuestsQueryBuilder` object that contains the refined query. */
  (query: EventGuestQuery, options?: QueryEventGuestsOptions): Promise<
    NonNullablePaths<
      QueryEventGuestsResponse,
      | `guests`
      | `guests.${number}.guestDetails.checkedIn`
      | `guests.${number}.guestDetails.onlineConferencingLogin.link`
      | `guests.${number}.attendanceStatus`
      | `guests.${number}.guestType`
      | `guests.${number}.additionalDetails.rsvpStatus`
      | `guests.${number}.additionalDetails.orderStatus`,
      6
    >
  >;
}

export const onGuestCreated = EventDefinition(
  'wix.events.guests.v1.guest_created',
  true,
  (event: GuestCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.attendanceStatusUpdatedDate' },
            { path: 'entity.guestDetails.formResponse.inputValues.dateTime' },
            { path: 'entity.additionalDetails.createdDate' },
            { path: 'entity.additionalDetails.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'entity.guestDetails.formResponse.inputValues.address.address',
            },
          ],
        },
      ])
    )
)<GuestCreatedEnvelope>();
export const onGuestDeleted = EventDefinition(
  'wix.events.guests.v1.guest_deleted',
  true,
  (event: GuestDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.attendanceStatusUpdatedDate' },
            { path: 'entity.guestDetails.formResponse.inputValues.dateTime' },
            { path: 'entity.additionalDetails.createdDate' },
            { path: 'entity.additionalDetails.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'entity.guestDetails.formResponse.inputValues.address.address',
            },
          ],
        },
      ])
    )
)<GuestDeletedEnvelope>();
export const onGuestCheckedIn = EventDefinition(
  'wix.events.guests.v1.guest_guest_checked_in',
  true,
  (event: GuestCheckedInEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.guest.createdDate' },
            { path: 'data.guest.updatedDate' },
            { path: 'data.guest.attendanceStatusUpdatedDate' },
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.dateTime',
            },
            { path: 'data.guest.additionalDetails.createdDate' },
            { path: 'data.guest.additionalDetails.updatedDate' },
            { path: 'data.eventDetails.dateAndTimeSettings.startDate' },
            { path: 'data.eventDetails.dateAndTimeSettings.endDate' },
            {
              path: 'data.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.guest.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.address.address',
            },
            { path: 'data.eventDetails.location.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.eventDetails.eventPageUrl' }],
        },
      ])
    )
)<GuestCheckedInEnvelope>();
export const onGuestEventCanceled = EventDefinition(
  'wix.events.guests.v1.guest_guest_event_canceled',
  true,
  (event: GuestEventCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.guest.createdDate' },
            { path: 'data.guest.updatedDate' },
            { path: 'data.guest.attendanceStatusUpdatedDate' },
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.dateTime',
            },
            { path: 'data.guest.additionalDetails.createdDate' },
            { path: 'data.guest.additionalDetails.updatedDate' },
            { path: 'data.event.dateAndTimeSettings.startDate' },
            { path: 'data.event.dateAndTimeSettings.endDate' },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.guest.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.address.address',
            },
            { path: 'data.event.location.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.event.eventPageUrl' }],
        },
      ])
    )
)<GuestEventCanceledEnvelope>();
export const onGuestEventStarts = EventDefinition(
  'wix.events.guests.v1.guest_guest_event_starts',
  true,
  (event: GuestEventStartsEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.guest.createdDate' },
            { path: 'data.guest.updatedDate' },
            { path: 'data.guest.attendanceStatusUpdatedDate' },
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.dateTime',
            },
            { path: 'data.guest.additionalDetails.createdDate' },
            { path: 'data.guest.additionalDetails.updatedDate' },
            { path: 'data.event.dateAndTimeSettings.startDate' },
            { path: 'data.event.dateAndTimeSettings.endDate' },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.guest.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.address.address',
            },
            { path: 'data.event.location.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.event.eventPageUrl' }],
        },
      ])
    )
)<GuestEventStartsEnvelope>();
export const onGuestOrderCanceled = EventDefinition(
  'wix.events.guests.v1.guest_guest_order_canceled',
  true,
  (event: GuestOrderCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.guest.createdDate' },
            { path: 'data.guest.updatedDate' },
            { path: 'data.guest.attendanceStatusUpdatedDate' },
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.dateTime',
            },
            { path: 'data.guest.additionalDetails.createdDate' },
            { path: 'data.guest.additionalDetails.updatedDate' },
            { path: 'data.event.dateAndTimeSettings.startDate' },
            { path: 'data.event.dateAndTimeSettings.endDate' },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.guest.guestDetails.formResponse.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'data.guest.guestDetails.formResponse.inputValues.address.address',
            },
            { path: 'data.event.location.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.event.eventPageUrl' }],
        },
      ])
    )
)<GuestOrderCanceledEnvelope>();
export const onGuestUpdated = EventDefinition(
  'wix.events.guests.v1.guest_updated',
  true,
  (event: GuestUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.attendanceStatusUpdatedDate' },
            { path: 'entity.guestDetails.formResponse.inputValues.dateTime' },
            { path: 'entity.additionalDetails.createdDate' },
            { path: 'entity.additionalDetails.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.attendanceStatusUpdatedDate' },
            {
              path: 'modifiedFields.guestDetails.formResponse.inputValues.dateTime',
            },
            { path: 'modifiedFields.additionalDetails.createdDate' },
            { path: 'modifiedFields.additionalDetails.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.guestDetails.formResponse.inputValues.number' },
            {
              path: 'modifiedFields.guestDetails.formResponse.inputValues.number',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            {
              path: 'entity.guestDetails.formResponse.inputValues.address.address',
            },
            {
              path: 'modifiedFields.guestDetails.formResponse.inputValues.address.address',
            },
          ],
        },
      ])
    )
)<GuestUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdditionalDetails,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  AttendanceStatus,
  BaseEventMetadata,
  CheckIn,
  CommonAddress,
  CommonAddressLocation,
  CommonAddressStreetOneOf,
  CommonStreetAddress,
  CommonSubdivision,
  ContactEventStatusUpdated,
  CouponDiscount,
  CursorPaging,
  Cursors,
  DateAndTimeSettings,
  Discount,
  DiscountItem,
  DiscountItemDiscountOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventDeleted,
  EventDetails,
  EventGuest,
  EventGuestQuerySpec,
  EventMetadata,
  EventType,
  Fee,
  FeeName,
  FeeType,
  FormResponse,
  FormSubmission,
  Formatted,
  FormattedAddress,
  GuestCheckedIn,
  GuestCheckedInEnvelope,
  GuestCount,
  GuestCountUpdate,
  GuestCountUpdated,
  GuestCreatedEnvelope,
  GuestDeletedEnvelope,
  GuestDetails,
  GuestEventCanceled,
  GuestEventCanceledEnvelope,
  GuestEventStarts,
  GuestEventStartsEnvelope,
  GuestListPreview,
  GuestOrderCanceled,
  GuestOrderCanceledEnvelope,
  GuestOrderConfirmed,
  GuestType,
  GuestUpdatedEnvelope,
  GuestsQueryBuilder,
  GuestsQueryResult,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  Invoice,
  Item,
  Language,
  ListGuestListPreviewsRequest,
  ListGuestListPreviewsResponse,
  Location,
  LocationType,
  MemberEventStatusUpdated,
  MemberEventStatusUpdatedEventType,
  MemberGuest,
  MemberGuests,
  MessageEnvelope,
  Money,
  NotifyActionType,
  NotifyGuestAction,
  Occurrence,
  OnlineConferencingLogin,
  OrderConfirmed,
  OrderDeleted,
  OrderDetails,
  OrderStatus,
  OrderType,
  OrderUpdated,
  Paging,
  PagingMetadataV2,
  PaidPlanDiscount,
  PaidPlanDiscountDiscountOneOf,
  PercentDiscount,
  QueryEventGuestsOptions,
  QueryEventGuestsRequest,
  QueryEventGuestsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RecurrenceStatusStatus,
  Recurrences,
  RequestedFields,
  RequestedFieldsEnumRequestedFields,
  RestoreInfo,
  RsvpStatus,
  SecondaryLanguagesRequest,
  SecondaryLanguagesResponse,
  SortOrder,
  Sorting,
  StandardDetails,
  Status,
  StreamEventGuestsRequest,
  StreetAddress,
  Subdivision,
  SubdivisionSubdivisionType,
  SubdivisionType,
  Task,
  TaskContext,
  TaskKey,
  Tax,
  TaxType,
  Ticket,
  TicketDetails,
  TicketGuestDetails,
  TicketingOrderStatus,
  Timing,
  UpdateGuestRequest,
  UpdateGuestResponse,
  WebhookIdentityType,
  utils,
} from './events-guests-v1-guest-guests.universal.js';
